import { COORDINATE_STOCKHOLM } from '@shared/constants';
import {
  ElementKind,
  type Baseline,
  type IBuildingFeatures,
  type Project,
  type ProjectMetadata,
} from '@shared/models/project.model';
import type {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import type { Storey } from '@shared/models/design/storey.model';
import {
  createDesignBuilding,
  isEmptyBuildingGeometry,
  isEmptyStoreysGeometry,
  withCompleteStoreyFields,
} from './design/storey.helpers';
import { applyMissingCalculatedAreaActivities } from './design/calculated-area-activities.helpers';
import {
  createBuildingDesign,
  ensureDesignHasBuilding,
} from './design/factories/design.factory';
import { createStoreys } from './design/factories/storey.factory';
import { createElementOfType } from './element-factory.helpers';
import { guards } from './typeguards.helpers';
import { withPersistentBuildingNames } from './design/building-name.helpers';

/**
 * Legacy {@link BuildingDesign} as persisted/exported before DEV-2576: storeys
 * lived directly on the design instead of inside {@link Building}s.
 */
type LegacyBuildingDesign = Omit<BuildingDesign, 'buildings'> & {
  buildings?: Building[];
  storeys?: Storey[];
};

/** Legacy `buildings[0]` wrapper that held the project geometry/baselines. */
type LegacyBuilding = {
  meta?: ProjectMetadata;
  design?: LegacyBuildingDesign;
  features?: IBuildingFeatures;
  versions?: Baseline[];
};

/**
 * Project as persisted/exported before DEV-2576: a single-element `buildings`
 * array wrapped `meta` / `design` / `features` / `versions`.
 */
type ProjectWithLegacyWrapper = Project & {
  buildings?: unknown;
};

const DEFAULT_PROJECT_META: ProjectMetadata = { design: { gfa: 0 } };

/**
 * True when {@link value} is a plain object, matching SQL `jsonb_typeof = 'object'`.
 * Strings, arrays, and null are rejected.
 */
const isProjectMetaObject = (
  value: unknown,
): value is Record<string, unknown> => guards.is.object(value);

/** True when meta already has a numeric `design.gfa` and needs no heal. */
const isCompleteProjectMeta = (value: unknown): value is ProjectMetadata => {
  if (!isProjectMetaObject(value) || !guards.is.object(value.design)) {
    return false;
  }
  return guards.is.number(value.design.gfa);
};

/**
 * Keep extra meta keys and fill `design.gfa` with 0 when it is missing.
 */
const ensureMetaGfa = (value: Record<string, unknown>): ProjectMetadata => {
  const design = guards.is.object(value.design) ? value.design : {};
  const gfa = guards.is.number(design.gfa) ? design.gfa : 0;
  return { ...value, design: { ...design, gfa } };
};

/**
 * Prefer complete flat {@link projectMeta}, then legacy {@link wrapperMeta}.
 * An empty `{}` or `{ design: {} }` must not wipe wrapper name/GFA.
 * Non-objects fall through to {@link DEFAULT_PROJECT_META}.
 */
const resolveProjectMeta = (
  projectMeta: unknown,
  wrapperMeta: unknown,
): ProjectMetadata => {
  if (isCompleteProjectMeta(projectMeta)) return ensureMetaGfa(projectMeta);
  if (isProjectMetaObject(wrapperMeta)) return ensureMetaGfa(wrapperMeta);
  if (isProjectMetaObject(projectMeta)) return ensureMetaGfa(projectMeta);
  return DEFAULT_PROJECT_META;
};

const isLegacyBuilding = (value: unknown): value is LegacyBuilding => {
  if (!guards.is.object(value)) return false;
  return (
    'versions' in value ||
    'design' in value ||
    'meta' in value ||
    'features' in value
  );
};

/** True when the object looks like a design, not an empty placeholder `{}`. */
const isLegacyBuildingDesign = (
  value: unknown,
): value is LegacyBuildingDesign => {
  if (!guards.is.object(value)) return false;
  return (
    'location' in value ||
    'activities' in value ||
    'storeys' in value ||
    'buildings' in value
  );
};

/** Narrow an untyped design document to {@link LegacyBuildingDesign}. */
const asLegacyBuildingDesign = (
  value: unknown,
): LegacyBuildingDesign | undefined =>
  isLegacyBuildingDesign(value) ? value : undefined;

/** True when the design already has drawable geometry to keep. */
const hasUsableDesignGeometry = (
  design: LegacyBuildingDesign | undefined,
): boolean => {
  if (!design) return false;
  if (
    design.buildings?.some((building) => !isEmptyBuildingGeometry(building))
  ) {
    return true;
  }
  if (!design.storeys?.length) return false;
  return !isEmptyStoreysGeometry(design.storeys);
};

/**
 * Prefer a design that already has drawable geometry. An empty `{}`,
 * `{ buildings: [] }`, or a factory placeholder with no shapes must not beat a
 * legacy wrapper that still has storeys.
 */
const pickUsableDesign = (
  preferred: LegacyBuildingDesign | undefined,
  fallback: LegacyBuildingDesign | undefined,
): LegacyBuildingDesign | undefined => {
  if (hasUsableDesignGeometry(preferred)) return preferred;
  if (hasUsableDesignGeometry(fallback)) return fallback;
  return preferred ?? fallback;
};

/**
 * Read the pre-DEV-2576 `buildings[]` wrapper when present on a project document.
 */
const getLegacyBuildings = (project: Project): LegacyBuilding[] | undefined => {
  if (!('buildings' in project)) return undefined;
  const value: unknown = (project as ProjectWithLegacyWrapper).buildings;
  if (!guards.is.array(value)) return undefined;
  return value.filter(isLegacyBuilding);
};

/** Whether a design still needs wrapping into at least one {@link Building}. */
const designNeedsBuildingWrap = (
  design: LegacyBuildingDesign | undefined,
): boolean => !!design && !design.buildings?.length;

/** Seed a default storey so the building can pass storey-list validation. */
const withAtLeastOneStorey = (building: Building): Building => {
  if (!guards.is.array(building.storeys) || building.storeys.length === 0) {
    return { ...building, storeys: createStoreys() };
  }
  const storeys = building.storeys.map(withCompleteStoreyFields);
  return storeys.every((storey, index) => storey === building.storeys[index])
    ? building
    : { ...building, storeys };
};

/**
 * Wrap a legacy design's flat `storeys` into a single {@link Building} so it
 * matches the post-DEV-2576 {@link BuildingDesign}. Already-wrapped designs with
 * drawable geometry are returned unchanged. Empty placeholder `buildings` yield
 * to legacy `storeys` when those still have shapes. Empty `buildings` always
 * gets a single building so the result is a valid {@link Project.design}.
 */
const normalizeLegacyDesignShape = (
  design: LegacyBuildingDesign | undefined,
): BuildingDesign => {
  if (!design) return createBuildingDesign();
  const { storeys, buildings, ...rest } = design;
  // Incomplete JSON must match SQL flatten defaults before calculated-area heal.
  const designRest = {
    ...rest,
    location: rest.location ?? COORDINATE_STOCKHOLM,
    activities: guards.is.array(rest.activities) ? rest.activities : [],
  };
  const hasDrawableBuilding = buildings?.some(
    (building) => !isEmptyBuildingGeometry(building),
  );
  if (hasDrawableBuilding && buildings) {
    return { ...designRest, buildings: buildings.map(withAtLeastOneStorey) };
  }
  if (storeys?.length && !isEmptyStoreysGeometry(storeys)) {
    return { ...designRest, buildings: [createDesignBuilding(storeys)] };
  }
  if (buildings?.length) {
    return { ...designRest, buildings: buildings.map(withAtLeastOneStorey) };
  }
  return {
    ...designRest,
    buildings: [
      createDesignBuilding(storeys?.length ? storeys : createStoreys()),
    ],
  };
};

/**
 * Normalize a legacy {@link Project} document (old `buildings[]` wrapper and/or
 * `design.storeys`) into the flattened DEV-2576 shape with `baselines`, `design`,
 * `meta` and `features` directly on the project.
 *
 * Idempotent: an already-flat project with at least one building, one baseline,
 * meta, and calculated-area activities is returned by reference so callers can
 * cheaply skip writes when nothing changed. Missing calculated-area refs are
 * repaired in place. Always returns a {@link Project} whose `design.buildings`
 * and `baselines` have length ≥ 1.
 * @param project Project document, possibly in the legacy shape.
 */
export const normalizeLegacyProjectShape = (project: Project): Project => {
  const legacyBuildings = getLegacyBuildings(project);
  const hasLegacyWrapper = guards.is.defined(legacyBuildings);
  const wrapper = legacyBuildings?.[0];
  const projectDesign = asLegacyBuildingDesign(project.design);
  const wrapperDesign = wrapper?.design;
  const rawDesign = pickUsableDesign(projectDesign, wrapperDesign);

  const hasValidBuildings =
    guards.is.array(project.design?.buildings) &&
    project.design.buildings.length > 0;
  const hasBuildingWithoutStoreys =
    hasValidBuildings &&
    project.design.buildings.some(
      (building) =>
        !guards.is.array(building.storeys) || building.storeys.length === 0,
    );
  // Empty placeholder buildings must not hide leftover storeys on the same object.
  const hasOrphanLegacyStoreys =
    guards.is.defined(projectDesign) &&
    !projectDesign.buildings?.some(
      (building) => !isEmptyBuildingGeometry(building),
    ) &&
    guards.is.array(projectDesign.storeys) &&
    projectDesign.storeys.length > 0 &&
    !isEmptyStoreysGeometry(projectDesign.storeys);
  // Drawable buildings plus a leftover `storeys` key still need normalize so
  // the stale key is stripped (SQL flatten always does `design - 'storeys'`).
  const hasLeftoverStoreysKey =
    guards.is.defined(projectDesign) && 'storeys' in projectDesign;

  const hasIncompleteMeta = !isCompleteProjectMeta(project.meta);
  const hasIncompleteDesignDefaults =
    !guards.is.array(project.design?.activities) || !project.design?.location;
  const hasBaselines =
    guards.is.array(project.baselines) && project.baselines.length > 0;
  if (
    !hasLegacyWrapper &&
    !designNeedsBuildingWrap(rawDesign) &&
    hasValidBuildings &&
    !hasOrphanLegacyStoreys &&
    !hasLeftoverStoreysKey &&
    !hasBuildingWithoutStoreys &&
    !hasIncompleteMeta &&
    !hasIncompleteDesignDefaults &&
    hasBaselines
  ) {
    const repairedDesign = withPersistentBuildingNames(
      applyMissingCalculatedAreaActivities(project.design),
    );
    if (repairedDesign === project.design) return project;
    return { ...project, design: repairedDesign };
  }

  const resolvedBaselines =
    (hasBaselines ? project.baselines : wrapper?.versions) ?? [];
  const baselines =
    resolvedBaselines.length > 0
      ? resolvedBaselines
      : [createElementOfType(ElementKind.Version)];
  const meta = resolveProjectMeta(project.meta, wrapper?.meta);
  const features = project.features ?? wrapper?.features;
  const design = withPersistentBuildingNames(
    applyMissingCalculatedAreaActivities(normalizeLegacyDesignShape(rawDesign)),
  );

  const { buildings: _legacyBuildings, ...projectRest } =
    project as ProjectWithLegacyWrapper;

  return {
    ...projectRest,
    baselines,
    design,
    meta,
    ...(features ? { features } : {}),
  };
};

/**
 * Rebuild the pre-DEV-2576 `buildings[]` wrapper so PaginationDB can write
 * through the legacy `buildings` column before flatten has run.
 * Empty {@link BuildingDesign.buildings} is healed to one storey first.
 * Throws when there is more than one building — the legacy column only stored
 * one wrapper and cannot round-trip siblings.
 */
export const toLegacyProjectWriteRow = (
  project: Project,
): Project & { buildings: LegacyBuilding[] } => {
  if (project.design.buildings.length > 1) {
    throw new Error(
      `Cannot write ${project.design.buildings.length} buildings through the legacy buildings column; flatten must run first`,
    );
  }
  const design = ensureDesignHasBuilding(project.design);
  const { buildings: _wrapped, ...designRest } = design;
  const firstBuilding = design.buildings[0];
  if (!firstBuilding) {
    throw new Error('Cannot write a legacy row without a building');
  }
  const storeys = firstBuilding.storeys;
  return {
    ...project,
    design,
    buildings: [
      {
        meta: project.meta,
        design: { ...designRest, storeys },
        features: project.features,
        versions: project.baselines,
      },
    ],
  };
};
